import { PriceEntryMode } from '../types';
import type { BatchStockInfo, CartItem, Item, SaleItemRequest } from '../types';
import { tracksBatches } from './inventoryMode';

export const roundMoney = (value: number): number => Math.round((value + Number.EPSILON) * 100) / 100;

export const calculateLineTotal = (quantity: number, unitPrice: number): number =>
  roundMoney(quantity * roundMoney(unitPrice));

export interface SalePriceBreakdown {
  enteredUnitPrice: number;
  unitNetPrice: number;
  unitGrossPrice: number;
  vatRate: number;
  netAmount: number;
  vatAmount: number;
  grossAmount: number;
  priceEntryMode: PriceEntryMode;
}

const normalizedRate = (vatRate?: number): number => roundMoney(vatRate ?? 0);

const amountsFromUnitPrices = (
  enteredUnitPrice: number,
  unitNetPrice: number,
  unitGrossPrice: number,
  vatRate: number,
  quantity: number,
  priceEntryMode: PriceEntryMode,
): SalePriceBreakdown => {
  const netAmount = calculateLineTotal(quantity, unitNetPrice);
  const grossAmount = calculateLineTotal(quantity, unitGrossPrice);
  return {
    enteredUnitPrice: roundMoney(enteredUnitPrice),
    unitNetPrice: roundMoney(unitNetPrice),
    unitGrossPrice: roundMoney(unitGrossPrice),
    vatRate: normalizedRate(vatRate),
    netAmount,
    vatAmount: roundMoney(grossAmount - netAmount),
    grossAmount,
    priceEntryMode,
  };
};

/** Mirrors the backend TaxCalculator rounding policy for sale-time service prices. */
export const calculateEnteredPriceBreakdown = (
  enteredUnitPrice: number,
  priceEntryMode: PriceEntryMode = PriceEntryMode.VAT_EXCLUDED,
  vatRate = 0,
  quantity = 1,
): SalePriceBreakdown => {
  const entered = roundMoney(enteredUnitPrice);
  const rate = normalizedRate(vatRate);
  if (priceEntryMode === PriceEntryMode.VAT_INCLUDED) {
    const unitNet = roundMoney(entered / (1 + rate / 100));
    return amountsFromUnitPrices(entered, unitNet, entered, rate, quantity, priceEntryMode);
  }
  const unitVat = roundMoney(entered * rate / 100);
  const unitGross = roundMoney(entered + unitVat);
  return amountsFromUnitPrices(entered, entered, unitGross, rate, quantity, priceEntryMode);
};

/** Uses API-provided canonical net/gross catalog prices without re-deriving them. */
export const calculateStoredPriceBreakdown = (
  unitNetPrice: number,
  unitGrossPrice: number,
  vatRate = 0,
  quantity = 1,
  priceEntryMode: PriceEntryMode = PriceEntryMode.VAT_EXCLUDED,
): SalePriceBreakdown => {
  const entered = priceEntryMode === PriceEntryMode.VAT_INCLUDED ? unitGrossPrice : unitNetPrice;
  return amountsFromUnitPrices(
    entered,
    roundMoney(unitNetPrice),
    roundMoney(unitGrossPrice),
    normalizedRate(vatRate),
    quantity,
    priceEntryMode,
  );
};

export const calculateCartTotals = (items: CartItem[]) => items.reduce(
  (totals, item) => ({
    netAmount: roundMoney(totals.netAmount + item.netAmount),
    vatAmount: roundMoney(totals.vatAmount + item.vatAmount),
    grossAmount: roundMoney(totals.grossAmount + item.totalPrice),
  }),
  { netAmount: 0, vatAmount: 0, grossAmount: 0 },
);

/** The price displayed for the selected batch is the only client-side sale price source. */
export const getCanonicalUnitPrice = (
  item: Item,
  batches: BatchStockInfo[],
  batchId?: string,
): number => {
  if (tracksBatches(item) && batchId) {
    return roundMoney(
      batches.find(batch => batch.batchId === batchId)?.unitPrice
      ?? item.priceGross
      ?? item.price
      ?? 0,
    );
  }
  return roundMoney(item.priceGross ?? item.price ?? 0);
};

export const toSaleItemRequest = (item: CartItem): SaleItemRequest => ({
  itemId: item.itemId,
  quantity: item.quantity,
  unitPrice: roundMoney(item.enteredUnitPrice),
  batchId: item.batchId,
});
